import {Controller} from '@hotwired/stimulus';
import {patch} from '@rails/request.js'

export default class extends Controller {
  static targets = [
    'date',
    'startTime',
    'endTime',
    'newPaymentMethodFormContainer',
    'stripeForm',
    'clientSecretField',
    'submitOrderButton',
    'summaryForm',
    'newPaymentIdField',
    'stripeFormError',
    'paymentMethodSelect',
    'scaAfterSubmitClientSecret',
    'paymentIntentIdField',
    'editOrderForm'
  ];

  static values = {
    stripeKey: String,
    edit: Boolean
  }

    async changeProperty(event) {
        const updatePath = event.target.dataset.updatePath;
        const property_id = event.target.value;

        await patch(
            updatePath,
            {
                body: JSON.stringify({property_id}),
                /* If you tell this, turbo will automatically render the stream that is returned */
                responseKind: 'turbo-stream'
            },
        );
    }

  async changeCartQty(event) {
    const updatePath = event.target.dataset.updatePath;
    const quantity = event.target.value;

    await patch(
      updatePath,
      {
        body: JSON.stringify({quantity}),
        /* If you tell this, turbo will automatically render the stream that is returned */
        responseKind: 'turbo-stream'
      },
    );
  }

  async updateTimeslot(event) {
    const updatePath = event.target.dataset.updatePath;
    await patch(
      updatePath,
      {
        body: JSON.stringify({
          date: this.dateTarget.value,
          startTime: this.startTimeTarget.value,
          endTime: this.endTimeTarget.value
        }),
        responseKind: 'turbo-stream'
      },
    );
  }

  initializeStripePaymentForm() {
    window.stripeClient = Stripe(this.stripeKeyValue);
    window.elements = window.stripeClient.elements({clientSecret: this.clientSecretFieldTarget.value});

    const paymentElement = window.elements.create('payment');
    paymentElement.mount(this.stripeFormTarget);
  }

  destroyStripePaymentForm() {
    if (window.elements) {
      window.elements.getElement('payment')?.unmount();
    }
    if (window.stripeClient) {
      window.stripeClient = null;
    }
    this.showStripeFormError('');
    this.newPaymentMethodFormContainerTarget.classList.add('d-none');
  }

  onSubmitClicked(event) {
    this.showStripeFormError('');
    this.summaryFormTarget.ariaBusy = true;

    if (!window.stripeClient || !window.elements) {
      /* Just a normal submit */
      return;
    }

    /* User was adding the card */
    event.preventDefault();

    window.stripeClient.confirmSetup({
      elements: window.elements,
      redirect: 'if_required',
    }).then((result) => {
      if (result.error) {
        this.showStripeFormError(result.error.message);
        this.summaryFormTarget.ariaBusy = false;
      } else {
        this.onAddPaymentSuccess(result, event.target);
      }
    }).catch(error => {
      this.showStripeFormError(error.message);
      this.summaryFormTarget.ariaBusy = false;
    });
  }

  showStripeFormError(message) {
    this.stripeFormErrorTarget.innerText = message;
  }

  onAddPaymentSuccess(result, button) {
    this.newPaymentIdFieldTarget.value = result.setupIntent.payment_method;
    this.summaryFormTarget.requestSubmit(button);
    this.summaryFormTarget.ariaBusy = false;
  }

  paymentSelectChanged(event) {
    const selectedPayment = event.target.value;
    if (!selectedPayment) {
      this.newPaymentMethodFormContainerTarget.classList.remove('d-none');
      this.initializeStripePaymentForm();
    } else {
      this.destroyStripePaymentForm();
    }
  }

  newPaymentMethodFormContainerTargetConnected() {
    /* Check if there's no other payment methods and initialize stripe form in that case */
    if (!this.hasPaymentMethodSelectTarget) {
      this.newPaymentMethodFormContainerTarget.classList.remove('d-none');
      this.initializeStripePaymentForm();
    }
  }

  /**
   * Waits for the SCA client secret to appear on page, takes it and presents the challenge to the user.
   * If the challenge is successful, it submits the form with the payment intent ID.
   */
  scaAfterSubmitClientSecretTargetConnected() {
    const form = this.editValue ? this.editOrderFormTarget : this.summaryFormTarget;

    form.ariaBusy = true;
    const clientSecret = this.scaAfterSubmitClientSecretTarget.value;

    if (!window.stripeClient) {
      window.stripeClient = Stripe(this.stripeKeyValue);
    }

    window.stripeClient.handleCardAction(clientSecret)
      .then(res => {
        if (res.error) {
          throw new Error(res.error.message);
        } else {
          this.paymentIntentIdFieldTarget.value = res.paymentIntent.id;
          form.requestSubmit();
        }
      })
      .catch(error => {
        this.stripeFormErrorTarget.innerText = error.message;
        form.ariaBusy = false;
      });
  }
}
